import React, { useState } from 'react';
import {
  Envelope,
  Shield,
  UserPlus,
  UsersThree,
  WarningCircle,
  X
} from '@phosphor-icons/react';

/**
 * Invite Member Modal - Phase B: Team Management
 * Handles user invitation with role selection and seat assignment
 */
const InviteMemberModal = ({ isOpen, onClose, onInvite, billing, loading }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('member');
  const [assignSeat, setAssignSeat] = useState(true); // Auto-checked for member/viewer
  const [errors, setErrors] = useState({});

  // Auto-check seat assignment — all roles consume a seat
  React.useEffect(() => {
    setAssignSeat(true);
  }, [role]);

  if (!isOpen) return null;

  const currentPlan = billing?.plan_tier || billing?.plan;
  const canAssignSeat = billing?.seats_available > 0;

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = () => {
    const newErrors = {};
    
    if (!email || !validateEmail(email)) {
      newErrors.email = 'Valid email is required';
    }
    
    if (!canAssignSeat) {
      newErrors.seat = 'No seats available. Purchase more seats to add team members.';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    onInvite({ email, name, role, assign_seat: assignSeat });
  };

  const handleClose = () => {
    setEmail('');
    setName('');
    setRole('member');
    setAssignSeat(true); // Default to true since member is default role
    setErrors({});
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg z-50 px-4 max-h-[90vh]">
        <div className="glass rounded-2xl border border-[rgba(255,255,255,0.2)] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="relative px-8 py-6 border-b border-[rgba(255,255,255,0.1)]">
            <button
              onClick={handleClose}
              className="absolute top-6 right-6 text-gray-400 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#6366F1]/20 rounded-xl flex items-center justify-center">
                <UserPlus size={24} className="text-[#6366F1]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Invite Team Member</h2>
                <p className="text-sm text-gray-400">Add a new user to your organization</p>
              </div>
            </div>
          </div>

          {/* Content - Scrollable */}
          <div className="px-8 py-6 space-y-5 overflow-y-auto max-h-[55vh]">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Envelope size={16} className="inline mr-2" />
                Email Address *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrors({}); }}
                placeholder="colleague@company.com"
                className="w-full px-4 py-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
              />
              {errors.email && (
                <p className="mt-2 text-sm text-red-400">{errors.email}</p>
              )}
            </div>

            {/* Name (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Full Name (Optional)
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                className="w-full px-4 py-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
              />
            </div>

            {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Shield size={16} className="inline mr-2" />
                Role *
              </label>
              <div className="space-y-3">
                {/* Member Option */}
                <label className="flex items-start p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg cursor-pointer hover:border-[#6366F1] transition-colors">
                  <input
                    type="radio"
                    name="role"
                    value="member"
                    checked={role === 'member'}
                    onChange={(e) => { setRole(e.target.value); setErrors({}); }}
                    className="mt-1 text-[#6366F1]"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center gap-2">
                      <UsersThree size={16} className="text-blue-400" />
                      <span className="font-medium text-white">Member</span>
                    </div>
                    <p className="text-sm text-gray-400 mt-1">
                      Can run workers and workflows. Does not have access to manage users or settings.
                    </p>
                  </div>
                </label>

                {/* Admin Option */}
                <label className="flex items-start p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg cursor-pointer hover:border-[#6366F1] transition-colors">
                  <input
                    type="radio"
                    name="role"
                    value="admin"
                    checked={role === 'admin'}
                    onChange={(e) => { setRole(e.target.value); setErrors({}); }}
                    className="mt-1 text-[#6366F1]"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center gap-2">
                      <Shield size={16} className="text-purple-400" />
                      <span className="font-medium text-white">Admin</span>
                    </div>
                    <p className="text-sm text-gray-400 mt-1">
                      Full access to manage users, workers, integrations, and organisation settings.
                    </p>
                  </div>
                </label>
              </div>
              {errors.role && (
                <p className="mt-2 text-sm text-red-400">{errors.role}</p>
              )}
            </div>

            {/* Seat Info */}
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <div className="flex items-center gap-2">
                <UsersThree size={16} className="text-blue-400" />
                <span className="text-sm text-gray-300">
                  {canAssignSeat 
                    ? `${billing?.seats_available} seat${billing?.seats_available !== 1 ? 's' : ''} available — both Admin and Member roles consume a seat`
                    : 'No seats available. All roles require a seat.'}
                </span>
              </div>
              {errors.seat && (
                <p className="mt-2 text-sm text-red-400">{errors.seat}</p>
              )}
            </div>

            {/* Info Banner */}
            <div className="p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg">
              <div className="flex items-start gap-3">
                <WarningCircle size={16} className="text-gray-400 mt-0.5" />
                <p className="text-sm text-gray-400">
                  An invitation email will be sent to this address. The invited user will consume one seat regardless of their role.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-8 py-6 border-t border-[rgba(255,255,255,0.1)] flex justify-end gap-3">
            <button
              onClick={handleClose}
              disabled={loading}
              className="px-6 py-3 glass text-white rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-3 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                'Sending...'
              ) : (
                <>
                  <UserPlus size={18} />
                  Send Invitation
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default InviteMemberModal;
